import { forwardRef, type InputHTMLAttributes, type ReactNode } from 'react';
import { cn } from '@/shared/lib/cn';

export interface RadioProps
  extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type'> {
  /** Optional inline label rendered after the control. */
  label?: ReactNode;
  invalid?: boolean;
}

const dotClasses =
  'h-4 w-4 shrink-0 ' +
  'accent-[var(--button-primary-bg)] ' +
  'transition-colors duration-fast ease-standard ' +
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--field-focus-ring)] ' +
  'disabled:cursor-not-allowed disabled:opacity-60';

/**
 * Radio field primitive — the single-select counterpart to Checkbox. Group
 * several by sharing a `name`. Shares the field focus-ring + accent tokens.
 */
export const Radio = forwardRef<HTMLInputElement, RadioProps>(function Radio(
  { className, label, invalid = false, disabled, ...rest },
  ref,
) {
  const dot = (
    <input
      ref={ref}
      type="radio"
      aria-invalid={invalid || undefined}
      disabled={disabled}
      className={cn(dotClasses, className)}
      {...rest}
    />
  );
  if (label === undefined) return dot;
  return (
    <label
      className={cn(
        'inline-flex items-center gap-2 text-sm text-[var(--text-primary)]',
        disabled && 'cursor-not-allowed opacity-60',
      )}
    >
      {dot}
      <span>{label}</span>
    </label>
  );
});
